import { useCallback } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import useToggleIncluded from '@/api/mutations/useToggleIncluded';
import { QueryEntity } from '@/api/types';
import { cn } from '@/helpers/shadCnUtils';
import { FactDropdown } from '@/new/components/fact';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/new/components/shadcn/empty';
import { Fact } from '@/types';

interface FactEditorTableProps {
  facts: Fact[];
  selectedFactId: string;
  keyFactIdSet: Set<string>;
  openDocViewer: (docId: string, factId: string, pageNumber: number) => void;
}

export const FactsTable = ({ facts, keyFactIdSet, selectedFactId, openDocViewer }: FactEditorTableProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) {
    return;
  }
  const queryClient = useQueryClient();
  const { mutate: mutateUpdateIncluded } = useToggleIncluded();

  const handleFactToggle = useCallback(
    (checked: boolean | string, eventId: string) => {
      mutateUpdateIncluded(
        { caseId: matterId, toggleValue: Boolean(checked), eventId },
        {
          onError: () => {
            toast.error('Failed to select item');
          },
          onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryEntity.CaseFacts, { caseId: matterId }] });
            queryClient.invalidateQueries({ queryKey: [QueryEntity.IncludedFactsCount, { caseId: matterId }] });
          },
        },
      );
    },
    [matterId, mutateUpdateIncluded],
  );

  return (
    <div className="h-full">
      {facts.length === 0 ? (
        <div className="flex w-full items-center justify-center py-6">
          <Empty className="max-w-md border border-gray-200 bg-gray-50/50">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <span className="iconify lucide--file-question h-6 w-6" />
              </EmptyMedia>
              <EmptyTitle>No facts found</EmptyTitle>
              <EmptyDescription>Sorry, we couldn't find any facts matching these criteria.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      ) : (
        <Accordion type="multiple" className="flex h-full flex-col gap-2 pr-1">
          {facts.map((fact) => (
            <div key={fact.id} className="flex w-full items-center gap-2.5">
              <Checkbox
                className="h-5 w-5 rounded-full"
                checked={fact.included}
                onCheckedChange={(checked) => handleFactToggle(checked, fact.id)}
              />
              <FactDropdown
                fact={fact}
                onViewDocument={openDocViewer}
                className={cn(
                  'w-full rounded-lg border',
                  selectedFactId === fact.id
                    ? `border-brandSecondary`
                    : keyFactIdSet && keyFactIdSet.has(fact.id) && `border-yellow-300 bg-yellow-50/20`,
                )}
              />
            </div>
          ))}
        </Accordion>
      )}
    </div>
  );
};
